import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useHistory } from 'react-router-dom';
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise'
import { showErrorModal, setLoading, useApplicationState } from '../../utilities/applicationState';
import { themeAlpine } from 'ag-grid-community';
import axios from 'axios';
import { ListPlaceHolder } from '../common/placeholder';
import { get } from 'lodash';
import {
  SdfAlert,
  SdfBusyIndicator,
  SdfButton,
  SdfGrid,
  SdfIcon,
  SdfInput,
  SdfLink,
  SdfMultiSelect,
  SdfSelect,
  SdfShimmer,
  SdfTag,
  SdfxPagination
} from '@synerg/react-components';
import './partners.scss';
import PartnerOrgDetails from './partnerOrgDetails';
import ViewPartnerProjects from './viewPartnerProjects';
import AddExisitingPartnerModal from './addExisitingPartnerModal';
import ImportLegacyProject from '../importLegacyProject/importLegacyProject';
import AddNewPartnerModal from './modals/addNewPartner';
import Toast from '../common/toast';
import { PAGE_SIZES } from '../constants';

const agGridKey = window.SynergConfig.getKey('ag-grid-enterprise');
LicenseManager.setLicenseKey(agGridKey);

const AVAILABLE_PAGE_SIZES = [];

export default function ProjectListTable() {
  const [appState, dispatch] = useApplicationState();
  const history = useHistory();
  const ref = useRef();
  const grid = useRef();
  const prevSortOrderRef = useRef({ colId: 'createdDate', sortBy: -1 });
  const [isBusy, setIsBusy] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [reportDownloaded, setReportDownloaded] = useState(false);
  const [totalData, setTotalData] = useState('');
  const [showView, setShowView] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState({});
  const [viewProjectsClicked, setViewProjectsClicked] = useState(false);
  const [deletedPartner, setDeletedPartner] = useState(false);
  const [showAddExistingPartner, setShowAddExistingPartner] = useState(false);
  const [addedExistingPartner, setAddedExistingPartner] = useState(false);
  const [showSlideInModal, setShowSlideInModal] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [searchedOOID, setSearchedOOID] = useState(null);
  const [addedNewPartner, setAddedNewPartner] = useState(false);
  const [toast, setToast] = useState(false);

  // available filter
  const [searchBy, setSearchBy] = useState('');
  const [allPartnerContacts, setAllPartnerContacts] = useState([]);
  const [allBusinessContacts, setAllBusinessContacts] = useState([]);
  const [allTechnicalContacts, setAllTechnicalContacts] = useState([]);
  const [businessContacts, setBusinessContacts] = useState([]);
  const [technicalContacts, setTechnicalContacts] = useState([]);
  const [partnerContacts, setPartnerContacts] = useState([]);
  const [showColumns, setShowColumns] = useState(false);
  const [legacyOrg, setLegacyOrg] = useState({});

  // pagination
  const [selectedPaginationOptions, setSelectedPaginationOptions] = useState({
    totalPages: 1,
    pageSize: 10,
    page: 1,
    skip: 0
  });
  const [start, setStart] = useState(1);
  const [end, setEnd] = useState(10);
  const [onChangePageSize, setOnChangePageSize] = useState(10);
  const [onChangePage, setOnChangePage] = useState(1);
  const [cancelableRequest, setCancelableRequest] = useState(false);
  const [filterChange, setFilterChange] = useState(null);
  const [preSelectedTab, setPreSelectedTab] = useState("Members");

  const [sortOrder, setSortOrder] = useState({ colId: 'createdDate', sortBy: -1 });

  const myIcons = {
    first: "<sdf-icon icon='nav-page-first'></sdf-icon>",
    previous: "<sdf-icon icon='nav-page-back'></sdf-icon>",
    next: "<sdf-icon icon='nav-page-next'></sdf-icon>",
    last: "<sdf-icon icon='nav-page-last'></sdf-icon>",
    sortAscending: "<sdf-icon icon='action-sort-asc'></sdf-icon>",
    sortDescending: "<sdf-icon icon='action-sort-desc'></sdf-icon>",
    sortUnSort: "<sdf-icon icon='action-sort'></sdf-icon>"
  };

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: false,
    wrapText: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    unSortIcon: true,
    cellDataType: false,
    headerClass: 'font-bold'
  };

  const gridOptions = {
    defaultColDef: defaultColDef,
    pagination: false,
    suppressPaginationPanel: true,
    paginationPageSize: 10,
    suppressHorizontalScroll: true,
    theme: themeAlpine
  };

  const SORT_BY = {
    "partnerName": "orderByName",
    "numberOfProjects": "orderByNumberOfProjects",
    "createdDate": "orderByCreatedDate",
    "lastLogin": "orderByLastLogin",
    "partnerType": "orderByPartnerType",
    "technicalContacts.displayName": "orderByTechnicalContact",
    "businessContacts.displayName": "orderByBusinessContact",
    "partnerContacts.displayName": "orderByPartnerContact",
  }

  const determineSortOrder = (sortByCol) => {
    const determineAscOrDesc = () => {
      const retAscOrDesc = sortOrder.sortBy;
      if (retAscOrDesc === 1) {
        return 'asc';
      } else if (retAscOrDesc === -1) {
        return 'desc';
      } else {
        return null;
      }
    };

    switch (sortByCol) {
      case SORT_BY[sortOrder.colId]:
        return determineAscOrDesc();
      default:
        return null;
    }
  };

  const updateStartAndEnd = (total, pageNo, pageSize) => {
    setStart(total > 0 ? (pageNo - 1) * pageSize + 1 : 0);
    setEnd((pageNo * pageSize) > total ? total : (pageNo * pageSize));
  };

  const search = async () => {
    try {
      dispatch(setLoading(true));
      const { data: { partnerList, total, options } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partners`, {
        cancelToken: getCancelableRequestToken().token,
        params: getSearchParams()
      });
      if (allPartnerContacts.length < 1) {
        setAllPartnerContacts(options.filters.partnerContacts.map(item => ({ key: item, "label": item, "value": item })));
      }
      if (allTechnicalContacts.length < 1) {
        setAllTechnicalContacts(options.filters.technicalContacts.map(item => ({ key: item, "label": item, "value": item })));
      }
      if (allBusinessContacts.length < 1) {
        setAllBusinessContacts(options.filters.businessContacts.map(item => ({ key: item, "label": item, "value": item })));
      }
      setTableData(partnerList);
      setSelectedPaginationOptions({
        totalPages: Math.ceil(total / options.pageSize),
        skip: options.skipped,
        pageSize: options.pageSize || 10,
        page: Math.ceil(options.skipped / options.pageSize + 1) || 1,
      });
      updateStartAndEnd(total, Math.ceil(options.skipped / options.pageSize) + 1, options.pageSize);
      setTotalData(total);
      setIsBusy(false);
      dispatch(setLoading(false));
    } catch (e) {
      if (e && e.message !== 'search request canceled') {
        const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get Project List. Please try again.');
        dispatch(showErrorModal(errorMessage));
      }
    }
  };

  const getCancelableRequestToken = () => {
    if (cancelableRequest) {
      cancelableRequest.cancel('search request canceled');
    }
    const newRequest = axios.CancelToken.source();
    setCancelableRequest(newRequest);
    return newRequest;
  };

  const getSearchParams = (limit) => {
    const params = {
      skip: selectedPaginationOptions.skip,
      limit: limit === undefined ? selectedPaginationOptions.pageSize : limit,
      searchBy: searchBy.trim(),
      businessContacts: businessContacts.map(item => item.value),
      technicalContacts: technicalContacts.map(item => item.value),
      partnerContacts: partnerContacts.map(item => item.value),
      ...({
        [SORT_BY[sortOrder.colId]]: sortOrder.sortBy
      })
    };
    // remove non-zero falsey params
    return Object.keys(params).reduce((prev, key) => {
      if (params[key] || params[key] === 0) {
        prev[key] = params[key];
      }
      return prev;
    }, {});
  };

  const ViewLink = (props) => {
    return (
      <div>
        <SdfButton emphasis={"tertiary"} className={"whitespace-nowrap"}
                   onClick={() => {
                     setShowView(true);
                     setSelectedPartner(props.data);
                   }}
        >
          View
        </SdfButton>
      </div>
    );
  };

  const onViewProjectsClick = (props) => {
    history.push(`/contacts/partners?ooid=${props.data.ooid}`);
    setViewProjectsClicked(true);
    setSelectedPartner(props.data);
  };

  const downloadReport = async () => {
    await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partners/download`, {
      cancelToken: getCancelableRequestToken().token,
      params: getSearchParams(),
      responseType: 'blob',
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let headerLine = response.headers["content-disposition"];
      var match = headerLine.match(/filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i);
      var filename = match[3];
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      setReportDownloaded(false);
    });
    setReportDownloaded(false);
  };

  const fetchPartnerDetails = async (ooid) => {
    const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/details/${ooid}`);
    setViewProjectsClicked(true);
    setSelectedPartner(data);
  }

  useEffect(() => {
    const ooid = new URL(document.location.href).searchParams.get('ooid');
    if (ooid) {
      fetchPartnerDetails(ooid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sortChanged = prevSortOrderRef.current.colId !== sortOrder.colId ||
      prevSortOrderRef.current.sortBy !== sortOrder.sortBy;

    if (filterChange || onChangePageSize || onChangePage || sortChanged) {
      search();
    }

    prevSortOrderRef.current = sortOrder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChange, sortOrder, onChangePageSize, onChangePage]);

  useEffect(() => {
    if (addedExistingPartner) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedExistingPartner]);

  useEffect(() => {
    if (addedNewPartner) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedNewPartner]);

  useEffect(() => {
    if (reportDownloaded) {
      downloadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDownloaded]);

  useEffect(() => {
    if (tableData?.length > 0) {
      AVAILABLE_PAGE_SIZES.length = 0;
      if (totalData) {
        PAGE_SIZES.every((element) => {
          if (totalData > element.value) {
            AVAILABLE_PAGE_SIZES.push(element);
            return true;
          }
          else {
            AVAILABLE_PAGE_SIZES.push(element);
            return false;
          }
        })
      }
    }
  }, [totalData]);

  const removeTag = (tag) => {
    const updatedBusinessContacts = businessContacts.filter((contact) => contact !== tag);
    const updatedTechContacts = technicalContacts.filter((contact) => contact !== tag);
    const updatedPartnerContacts = partnerContacts.filter((contact) => contact !== tag);
    updateFilterBusinessContacts(updatedBusinessContacts);
    updateFilterPartnerContacts(updatedTechContacts);
    updateFilterTechnicalContacts(updatedPartnerContacts);
  };

  const clearAllButton = () => {
    setSelectedPaginationOptions((prev) => {
      return { ...prev, skip: 0, page: 1 }
    });
    updateFilterBusinessContacts([]);
    updateFilterPartnerContacts([]);
    updateFilterTechnicalContacts([]);
  };

  const updateOnSearch = v => setSearchBy(v) || updateFilter();
  const updateFilterBusinessContacts = v => setBusinessContacts(v) || updateFilter();
  const updateFilterTechnicalContacts = v => setTechnicalContacts(v) || updateFilter();
  const updateFilterPartnerContacts = v => setPartnerContacts(v) || updateFilter();
  const updateFilter = () => resetPagination() || setFilterChange(Date.now());
  const resetPagination = () => setSelectedPaginationOptions({ skip: 0, page: 1, pageSize: selectedPaginationOptions.pageSize });

  const sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressSyncLayoutWithGrid: true,
          suppressColumnMove: true,
          suppressColumnFilter: true,
          suppressColumnSelectAll: true,
          suppressColumnExpandAll: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: true,
          suppressValues: true,
        }
      }
    ]
  };

  const setSideBarVisible = useCallback(() => {
    const isSideBarVisible = grid.current.api.isSideBarVisible();
    const isToolPanelVisible = grid.current.api.isToolPanelShowing();
    if (isSideBarVisible && isToolPanelVisible) {
      grid.current.api.setSideBarVisible(false);
      grid.current.api.closeToolPanel('columns')
      setShowColumns(false);
    } else {
      grid.current.api.setSideBarVisible(true);
      grid.current.api.openToolPanel('columns')
      setShowColumns(true);
    }
  }, []);

  const hasNoCriteria = () => {
    return [
      searchBy.trim(),
      businessContacts,
      technicalContacts,
      partnerContacts
    ].every(f => !f || (Array.isArray(f) && f.length === 0));
  };

  const PartnerNameDisplay = (props) => {
    return (<div className="leading-none">
      <div className="text-md font-medium leading-lg">{props.data.orgName}</div>
      <label className="partner-name-subtext">{props.data.isiOrgCd}</label>
    </div>);
  }

  const numberOfProjectsDisplay = (props) => {
    return (<div className="flex">
      <div className="mr-3">{props.data.numberOfProjects}</div>
      <SdfLink className="font-medium" href='#' onClick={() => { onViewProjectsClick(props) }}>Details</SdfLink>
    </div>);
  }

  useEffect(() => {
    if (deletedPartner) {
      setDeletedPartner(false);
      setShowView(false);
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedPartner]);

  const colDefs = useMemo(() => [
    {
      field: "partnerName",
      headerName: "Partner name",
      sortable: true,
      sort: determineSortOrder('orderByName'),
      suppressMovable: true,
      cellRenderer: (event) => PartnerNameDisplay(event),
      pinned: 'left',
      toolPanelClass: "p-10",
      suppressColumnsToolPanel: true,
      minWidth: 230,
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "ooid",
      headerName: "OOID",
      sortable: false,
      suppressMovable: true,
      pinned: 'left',
      toolPanelClass: "p-10",
      suppressColumnsToolPanel: true,
      minWidth: 230,
      headerClass: "text-sm font-bold"
    },
    {
      field: "numberOfProjects",
      headerName: "No. of projects",
      sortable: true,
      sort: determineSortOrder('orderByNumberOfProjects'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      cellRenderer: (event) => numberOfProjectsDisplay(event),
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "createdDate",
      headerName: "Created date",
      sortable: true,
      sort: determineSortOrder('orderByCreatedDate'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "lastLogin",
      headerName: "Last login",
      sortable: true,
      sort: determineSortOrder('orderByLastLogin'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "partnerType",
      headerName: "Partner type",
      sortable: true,
      sort: determineSortOrder('orderByPartnerType'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0,
      cellRenderer: (params) => {
        const data = params && params.data ? params.data : {};
        // console.debug('partnerType cellRenderer', { ooid: data.ooid, partnerType: data.partnerType, fdxSupport: data.fdxSupport });
        const raw = data.fdxSupport;
        const isFdx = raw === true || raw === 'true' || raw === 1 || raw === '1';
        const display = isFdx ? 'B2C' : (data.partnerType || '');
        return (<div className="capitalize">{display}</div>);
      }
    },
    {
      field: "technicalContacts.displayName",
      headerName: "Technical advisor",
      sortable: true,
      sort: determineSortOrder('orderByTechnicalContact'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "businessContacts.displayName",
      headerName: "Business advisor",
      sortable: true,
      sort: determineSortOrder('orderByBusinessContact'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "partnerContacts.displayName",
      headerName: "Partner advisor",
      sortable: true,
      sort: determineSortOrder('orderByPartnerContact'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      headerClass: "text-sm font-bold",
      comparator: () => 0
    },
    {
      field: "view",
      headerName: "",
      sortable: false,
      suppressMovable: true,
      cellRenderer: (event) => ViewLink(event),
      suppressColumnsToolPanel: true,
      pinned: 'right',
      cellStyle: { textAlign: 'center' }
    }
  ], [sortOrder]);

  return (
    <div className="project-table-box">
      {addedExistingPartner && <SdfAlert
        status={'success'}
        closeable="true"
        toast={true}
        style={{ display: "block !important" }}
        onSdfCloseComplete={() => { setAddedExistingPartner(false); setLegacyOrg({}) }}
      >
        <div className="flex justify-between w-2xl leading-xl col-gap-4">
          <div>{`${legacyOrg.orgName || ''} imported successfully!`}</div>
          <SdfButton emphasis="secondary" className="-mt-1 mr-1" onClick={() => { setShowSlideInModal(true); setAddedExistingPartner(false) }}>Import legacy projects</SdfButton>
        </div>
      </SdfAlert>}

      {showAddExistingPartner && <AddExisitingPartnerModal legacyOrg={legacyOrg} setLegacyOrg={setLegacyOrg} setShowAddExistingPartner={setShowAddExistingPartner} setAddedExistingPartner={setAddedExistingPartner} setSearchedOOID={setSearchedOOID} />}
      <div className={"flex pt-10 mx-0 justify-between"}>
        <span className={"block w-lg text-3xl font-extrabold leading-xs tracking-xs"}>Partner list</span>
        <div>
          <SdfButton emphasis={"secondary"} onClick={() => { setShowAddExistingPartner(true) }}>Add existing partner</SdfButton>
          <SdfButton className="ml-5" onClick={() => { setShowAddPartner(true) }} emphasis={"primary"}>
            Add new partner
          </SdfButton>
        </div>
      </div>
      {appState.loading && <div className="project-list-load">
        <SdfBusyIndicator
          style={{ color: "#1a8099" }}
          size="xl"
          fullScreen={true}
        />
      </div>}
      {isBusy && <ListPlaceHolder filtersNumber={4} className="placeholder-table" style={{ height: '350px' }} />}
      {!isBusy && <div ref={ref} className="project-list-container">
        <div className="siblings">
          <div className={"bg-action-default-50 my-5 rounded-xl"}>
            <div className={"p-5 pt-10"}>
              <div className={"flex justify-between gap-1"}>
                <SdfInput
                  className={"w-1/3"}
                  label={"Search"}
                  placeholder={"Search by partner name, OOID or partner ID"}
                  value={searchBy}
                  onSdfInput={($event) => {
                    updateOnSearch($event.detail)
                  }}
                >
                  <SdfIcon slot={"after"} icon={"action-search"}></SdfIcon>
                </SdfInput>

                <div className="textbox-with-label">
                  <SdfMultiSelect
                    label={"Filter by Business Developmenet Adv."}
                    className=""
                    filterable={true}
                    items={allBusinessContacts}
                    value={businessContacts}
                    placeholder="Select..."
                    inputWidth={"lg"}
                    onSdfChange={({ detail }) => { updateFilterBusinessContacts(detail) }}
                  />
                </div>
                <div className="textbox-with-label">
                  <SdfMultiSelect
                    label={"Filter by Technical Adv."}
                    className=""
                    items={allTechnicalContacts}
                    filterable={true}
                    value={technicalContacts}
                    inputWidth={"lg"}
                    onSdfChange={({ detail }) => { updateFilterTechnicalContacts(detail) }}
                    placeholder="Select..."
                    defaultSearchTerm=""
                  />
                </div>
                <div className="textbox-with-label">
                  <SdfMultiSelect
                    label={"Filter by Partner Development Adv."}
                    className=""
                    items={allPartnerContacts}
                    filterable={true}
                    inputWidth={"lg"}
                    value={partnerContacts}
                    onSdfChange={({ detail }) => { updateFilterPartnerContacts(detail) }}
                    placeholder="Select..."
                    defaultSearchTerm=""
                  />
                </div>

              </div>
            </div>
            <div className={"flex flex-wrap tags-section pl-5 pb-10"}>
              {[...technicalContacts, ...businessContacts, ...partnerContacts].map((option, idx) => {
                return <SdfTag
                  key={`tag-${idx}`}
                  className="bg-white rounded-full mr-2 mt-4"
                  onClick={() => removeTag(option)}
                  value={option.value}
                />
              })}
              {[...technicalContacts, ...businessContacts, ...partnerContacts].length > 0 &&
                <SdfButton onClick={clearAllButton} className={'ml-2 mt-4'} variant={'text'}>Clear all</SdfButton>
              }
            </div>
          </div>
        </div>
        {
          (tableData.length > 0 || appState.loading) &&
          <>
            <div className={"flex my-4 mx-0 justify-between"}>
              <div className={"block w-lg text-3xl font-extrabold leading-xs tracking-xs"}>Results ({totalData})</div>
              <div>
                <SdfButton className="mr-5" icon="action-download" onClick={() => ref.current && setReportDownloaded(true)} variant="text" emphasis="tertiary">
                  <a href="#!" id="download-table-csv-anchor" style={{ display: 'none' }}>Download report</a>
                  Download report
                </SdfButton>
                <SdfButton emphasis="tertiary" variant="text" icon={showColumns ? "action-hide" : "action-show"} onClick={setSideBarVisible}> &nbsp;Show/Hide Columns</SdfButton>
              </div>
            </div>
            <SdfGrid className={"ag-theme-alpine"}>
              <AgGridReact
                ref={grid}
                rowHeight={60}
                headerHeight={60}
                domLayout='autoHeight'
                gridOptions={gridOptions}
                icons={myIcons}
                rowData={tableData}
                rowClass={"template-row pt-1 cursor-pointer"}
                autoSizeAllColumns={true}
                unSortIcon={true}
                sideBar={sideBar}
                columnDefs={colDefs}
                onSortChanged={($event) => {
                  if ($event.source === 'uiColumnSorted') {
                    const columnState = $event.api.getColumnState();
                    const sortedColumns = columnState.filter(col => col.sort != null);

                    if (sortedColumns.length > 0) {
                      const { colId, sort } = sortedColumns[0];
                      if (colId !== sortOrder.colId || (sort === 'asc' ? 1 : -1) !== sortOrder.sortBy) {
                        setSelectedPaginationOptions((prev) => ({ ...prev, skip: 0, page: 1 }));
                        setSortOrder({ colId: colId, sortBy: sort === 'asc' ? 1 : -1 });
                      }
                    } else {
                      const defaultSort = { colId: 'createdDate', sortBy: -1 };
                      if (sortOrder.colId !== defaultSort.colId || sortOrder.sortBy !== defaultSort.sortBy) {
                        setSelectedPaginationOptions((prev) => ({ ...prev, skip: 0, page: 1 }));
                        setSortOrder(defaultSort);
                      }
                    }
                  }
                }}
                onFirstDataRendered={(params) => {
                  if (params.api && sortOrder.colId === 'createdDate' && sortOrder.sortBy === -1) {
                    params.api.applyColumnState({
                      state: params.api.getColumnState().map(col => ({ ...col, sort: null })),
                      defaultState: { sort: null }
                    });
                  }
                }}
              />
            </SdfGrid>

            <div className="flex items-center justify-end pt-4 pb-20">
              <div className="text-sm text-action-default">Rows per page:</div>
              <SdfSelect
                className="w-20 text-sm text-action-default px-4 mb-1 border-r-1 border-t-0 border-l-0 border-b-0 border-dark border-solid"
                label="rows per page"
                hideLabel={true}
                items={AVAILABLE_PAGE_SIZES}
                onSdfChange={({ detail }) => {
                  setSelectedPaginationOptions((prev) => {
                    return { ...prev, page: 1, skip: 0, pageSize: detail.value }
                  });
                  setOnChangePageSize(detail.value);
                }}
                value={{ label: `${selectedPaginationOptions.pageSize}`, value: selectedPaginationOptions.pageSize }}
              />
              <div className="text-sm text-action-default px-4">{`${start} - ${end} items`}</div>
              {
                selectedPaginationOptions.totalPages >= 1 &&
                <SdfxPagination
                  currentPage={selectedPaginationOptions.page || 1}
                  totalPages={selectedPaginationOptions.totalPages || 0}
                  onSdfxPageChange={({ detail }) => {
                    setSelectedPaginationOptions((prev) => {
                      return { ...prev, page: detail, skip: (detail - 1) * prev.pageSize }
                    })
                    setOnChangePage(detail);
                  }}
                />
              }
            </div>
          </>
        }
        {!tableData.length && hasNoCriteria() && <div className="no-data-text">There are currently no projects created yet. Please check back later.</div>}
        {!tableData.length && !hasNoCriteria() && <div className="no-data-text">No results found. Please try entering a different search term or switching filters for better results.</div>}
        <div className={"flex-col items-center gap-8"}>
          {isBusy && (<SdfShimmer rows={10} />)}
        </div>
      </div >}
      {showView && (<PartnerOrgDetails getPartners={search} partner={selectedPartner} setShowView={setShowView} setDeletedPartner={setDeletedPartner} preSelectedTab={preSelectedTab} />)}
      {viewProjectsClicked && <ViewPartnerProjects
        partnerName={selectedPartner.orgName}
        clientId={selectedPartner.isiOrgCd}
        ooid={selectedPartner.ooid}
        totalProjects={selectedPartner.numberOfProjects}
        partner={selectedPartner}
        setViewProjects={setViewProjectsClicked}
        setShowView={setShowView}
        setPreSelectedTab={setPreSelectedTab}
      />}
      {showSlideInModal && <ImportLegacyProject searchedOOID={searchedOOID} setShowSlideInModal={setShowSlideInModal} />}
      {showAddPartner && <AddNewPartnerModal setShowAddPartner={setShowAddPartner} setAddedNewPartner={setAddedNewPartner} setToast={setToast} />}
      {toast && <Toast
        key={toast.key}
        status={toast.status}
        message={toast.message}
        showToast={toast.showToast}
        autoCloseAfter={30000}
      />}
    </div >
  );
}
